import { useState, useEffect } from 'react';
import { supabase, ContactMessage } from '../../../lib/supabase';

export default function MessagesManager() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('contact_messages')
      .select('*')
      .order('created_at', { ascending: false });
    setMessages(data || []);
    setLoading(false);
  };

  const markAsRead = async (id: string) => {
    await supabase.from('contact_messages').update({ is_read: true }).eq('id', id);
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
  };

  const toggleOpen = async (msg: ContactMessage) => {
    if (selectedId === msg.id) {
      setSelectedId(null);
      return;
    }
    setSelectedId(msg.id);
    if (!msg.is_read) {
      await markAsRead(msg.id);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    await supabase.from('contact_messages').delete().eq('id', id);
    setMessages((prev) => prev.filter((m) => m.id !== id));
    if (selectedId === id) setSelectedId(null);
    setDeleting(null);
  };

  const markAllRead = async () => {
    const unread = messages.filter((m) => !m.is_read).map((m) => m.id);
    if (unread.length === 0) return;
    await supabase.from('contact_messages').update({ is_read: true }).in('id', unread);
    setMessages((prev) => prev.map((m) => ({ ...m, is_read: true })));
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filtered = filter === 'unread' ? messages.filter((m) => !m.is_read) : messages;
  const unreadCount = messages.filter((m) => !m.is_read).length;

  const eventTypeColors: Record<string, string> = {
    'Boda': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    'Fiesta Privada': 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30',
    'Evento Corporativo': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'Club/Discoteca': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'Festival': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'Otro': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <i className="ri-mail-line text-cyan-400"></i>
            Mensajes de contacto
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-cyan-500 text-black text-xs font-bold rounded-full">
                {unreadCount} nuevo{unreadCount > 1 ? 's' : ''}
              </span>
            )}
          </h2>
          <p className="text-gray-500 text-sm mt-1">Solicitudes recibidas desde el formulario de contacto</p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-double-line"></i>
            Marcar todos como leídos
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex gap-2">
        {(['all', 'unread'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              filter === f
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40'
                : 'bg-gray-900 text-gray-500 border border-gray-800 hover:border-gray-600'
            }`}
          >
            {f === 'all' ? `Todos (${messages.length})` : `No leídos (${unreadCount})`}
          </button>
        ))}
      </div>

      {/* Lista de mensajes */}
      {filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-600">
          <i className="ri-mail-open-line text-5xl mb-4 block"></i>
          <p>{filter === 'unread' ? 'No tienes mensajes nuevos' : 'Aún no has recibido mensajes'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((msg) => (
            <div
              key={msg.id}
              className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                !msg.is_read
                  ? 'border-cyan-500/40 bg-cyan-500/5'
                  : 'border-gray-800 bg-gray-900/50'
              }`}
            >
              {/* Cabecera del mensaje */}
              <button
                onClick={() => toggleOpen(msg)}
                className="w-full flex items-center gap-4 px-5 py-4 text-left cursor-pointer hover:bg-white/5 transition-colors"
              >
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                  !msg.is_read ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-800 text-gray-400'
                }`}>
                  {msg.name.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold text-sm ${!msg.is_read ? 'text-white' : 'text-gray-300'}`}>
                      {msg.name}
                    </span>
                    {!msg.is_read && (
                      <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0"></span>
                    )}
                    {msg.event_type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${eventTypeColors[msg.event_type] || eventTypeColors['Otro']}`}>
                        {msg.event_type}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5 truncate">{msg.email}</p>
                  {selectedId !== msg.id && (
                    <p className="text-gray-600 text-xs mt-1 truncate">{msg.message}</p>
                  )}
                </div>

                {/* Fecha y flecha */}
                <div className="flex-shrink-0 text-right">
                  <p className="text-gray-600 text-xs">{formatDate(msg.created_at)}</p>
                  <i className={`ri-arrow-${selectedId === msg.id ? 'up' : 'down'}-s-line text-gray-600 text-sm mt-1 block`}></i>
                </div>
              </button>

              {/* Contenido expandido */}
              {selectedId === msg.id && (
                <div className="px-5 pb-5 border-t border-gray-800/50">
                  <div className="mt-4 space-y-4">
                    {/* Datos del remitente */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-black/40 rounded-lg px-4 py-3">
                        <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Nombre</p>
                        <p className="text-white text-sm font-medium">{msg.name}</p>
                      </div>
                      <div className="bg-black/40 rounded-lg px-4 py-3">
                        <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Email</p>
                        <a
                          href={`mailto:${msg.email}`}
                          className="text-cyan-400 text-sm hover:underline cursor-pointer"
                        >
                          {msg.email}
                        </a>
                      </div>
                      {msg.event_type && (
                        <div className="bg-black/40 rounded-lg px-4 py-3">
                          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Tipo de evento</p>
                          <span className={`text-xs px-2 py-1 rounded-full border ${eventTypeColors[msg.event_type] || eventTypeColors['Otro']}`}>
                            {msg.event_type}
                          </span>
                        </div>
                      )}
                      <div className="bg-black/40 rounded-lg px-4 py-3">
                        <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Recibido</p>
                        <p className="text-gray-300 text-sm">{formatDate(msg.created_at)}</p>
                      </div>
                    </div>

                    {/* Mensaje */}
                    <div className="bg-black/40 rounded-lg px-4 py-3">
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Mensaje</p>
                      <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                    </div>

                    {/* Acciones */}
                    <div className="flex gap-3">
                      <a
                        href={`mailto:${msg.email}?subject=Re: Solicitud de DJ - ${msg.event_type || 'Evento'}`}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-fuchsia-500/20 border border-cyan-500/40 text-cyan-400 text-sm rounded-lg hover:border-cyan-400 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-reply-line"></i>
                        Responder por email
                      </a>
                      <button
                        onClick={() => handleDelete(msg.id)}
                        disabled={deleting === msg.id}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg hover:border-red-500/50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        <i className="ri-delete-bin-line"></i>
                        {deleting === msg.id ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
